import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Novu } from "@novu/js";
import { useTranslation } from "react-i18next";
import { config } from "../../config";
import { useSessionStore } from "../../store/useSessionStore";
import { Toast } from "../Toast";
import { NotificationsDrawer } from "./NotificationsDrawer";
import { NotificationPreferencesSheet } from "./NotificationPreferencesSheet";
import { SmsConsentController } from "./SmsConsentController";
import {
  resolveTransactionsHeader,
  type TransactionsHeaderSlot,
} from "./resolveTransactionsHeader";

export interface InboxItem {
  id: string;
  title: string;
  body: string;
  isRead: boolean;
  /** ISO timestamp from Novu. Absent only when the feed omits it. */
  createdAt?: string;
}

interface InboxContextValue {
  unread: number;
  items: InboxItem[];
  open: boolean;
  setOpen: (open: boolean) => void;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  archive: (id: string) => Promise<void>;
  openPreferences: () => void;
  enabled: boolean;
  /** Partner in-app channel. A missing bind field stays true. */
  inAppEnabled: boolean;
  /** First inbox list has settled. */
  listReady: boolean;
  /** First inbox list failed. The bell stays so a blip does not hide history. */
  listFailed: boolean;
  /**
   * Home right slot.
   * Pending paints nothing. Bell hides the Need help title.
   * Help is the shared Need help header.
   */
  headerSlot: TransactionsHeaderSlot;
  /** True when headerSlot is "bell". */
  showBell: boolean;
}

const InboxContext = createContext<InboxContextValue | null>(null);

function toInboxItem(note: {
  id: string;
  subject?: string;
  body: string;
  isRead: boolean;
  createdAt?: string | Date;
}): InboxItem {
  return {
    id: note.id,
    title: note.subject || note.body,
    body: note.subject ? note.body : "",
    isRead: note.isRead,
    createdAt: noticeCreatedAt(note.createdAt),
  };
}

function noticeCreatedAt(value?: string | Date): string | undefined {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** Newest first. Missing createdAt sinks to the bottom. */
function sortNewestFirst(items: InboxItem[]): InboxItem[] {
  return [...items].sort((left, right) => {
    const leftAt = left.createdAt ? Date.parse(left.createdAt) : 0;
    const rightAt = right.createdAt ? Date.parse(right.createdAt) : 0;
    return rightAt - leftAt;
  });
}

export function useNovuInbox(): InboxContextValue | null {
  return useContext(InboxContext);
}

export function NovuInboxProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const novuSession = useSessionStore((state) => state.session.data?.novu);
  const linkId = useSessionStore((state) => state.session.data?.linkId ?? null);
  const [client, setClient] = useState<Novu | null>(null);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [listReady, setListReady] = useState(false);
  const [listFailed, setListFailed] = useState(false);
  // Realtime items the feed has not returned yet. The list can lag the event.
  const pendingRef = useRef<Map<string, InboxItem>>(new Map());
  const firstListSettled = useRef(false);

  const settleFirstList = useCallback((failed: boolean) => {
    if (firstListSettled.current) return;
    firstListSettled.current = true;
    setListFailed(failed);
    setListReady(true);
  }, []);

  const refresh = useCallback(async (active: Novu | null = client) => {
    if (!active) return;
    try {
      const listed = await active.notifications.list({
        archived: false,
        limit: 30,
      });
      if (!listed.data) {
        settleFirstList(true);
        return;
      }
      const listedItems = listed.data.notifications.map(toInboxItem);
      for (const item of listedItems) pendingRef.current.delete(item.id);
      setItems(
        sortNewestFirst([...pendingRef.current.values(), ...listedItems]),
      );
      const counted = await active.notifications.count({ read: false });
      const count =
        counted.data && "count" in counted.data ? counted.data.count : 0;
      setUnread(count);
      settleFirstList(false);
    } catch {
      settleFirstList(true);
    }
  }, [client, settleFirstList]);

  useEffect(() => {
    pendingRef.current.clear();
    firstListSettled.current = false;
    setListReady(false);
    setListFailed(false);
    if (!novuSession) {
      setClient(null);
      setItems([]);
      setUnread(0);
      return;
    }
    const applicationIdentifier =
      (config.NOVU_APPLICATION_IDENTIFIER ?? "").trim() ||
      novuSession.applicationIdentifier;
    const next = new Novu({
      applicationIdentifier,
      subscriberId: novuSession.subscriberId,
      subscriberHash: novuSession.subscriberHash,
      contextHash: novuSession.contextHash,
      context: novuSession.context,
      apiUrl: "https://api.novu.co",
    });
    setClient(next);
    const stop = next.on("notifications.notification_received", (event) => {
      const note = event.result;
      setToast(note.subject || note.body || t("notifications.title"));
      const item = toInboxItem(note);
      pendingRef.current.set(item.id, item);
      setItems((previous) => [
        item,
        ...previous.filter((existing) => existing.id !== item.id),
      ]);
      void refresh(next);
    });
    void refresh(next);
    return () => {
      stop();
    };
    // `refresh` closes over the previous client. The effect passes the new one.
    // Listing `refresh` would reconnect on every client update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novuSession, t]);

  const markRead = useCallback(
    async (id: string) => {
      if (!client) return;
      await client.notifications.read({ notificationId: id });
      const pending = pendingRef.current.get(id);
      if (pending) pendingRef.current.set(id, { ...pending, isRead: true });
      await refresh(client);
    },
    [client, refresh],
  );

  const archive = useCallback(
    async (id: string) => {
      if (!client) return;
      await client.notifications.archive({ notificationId: id });
      pendingRef.current.delete(id);
      await refresh(client);
    },
    [client, refresh],
  );

  const openPreferences = useCallback(() => {
    setOpen(false);
    setPreferencesOpen(true);
  }, []);

  const reopenPreferences = useCallback(() => setPreferencesOpen(true), []);

  // The popup replaces the sheet while it is open. No stacked popups.
  // The sheet already loaded the consent and stored it before calling this.
  const askSmsConsent = useCallback(() => {
    setPreferencesOpen(false);
  }, []);

  const openInbox = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) void refresh(client);
    },
    [client, refresh],
  );

  const inAppEnabled = novuSession != null && novuSession.inAppEnabled !== false;
  const headerSlot = resolveTransactionsHeader({
    hasNovuSession: novuSession != null,
    inAppEnabled,
    visibleCount: items.length,
    listReady,
    listFailed,
  });
  const showBell = headerSlot === "bell";

  const value = useMemo<InboxContextValue>(
    () => ({
      unread,
      items,
      open,
      setOpen: openInbox,
      refresh: () => refresh(client),
      markRead,
      archive,
      openPreferences,
      enabled: Boolean(novuSession),
      inAppEnabled,
      listReady,
      listFailed,
      headerSlot,
      showBell,
    }),
    [
      unread,
      items,
      open,
      openInbox,
      refresh,
      client,
      markRead,
      archive,
      openPreferences,
      novuSession,
      inAppEnabled,
      listReady,
      listFailed,
      headerSlot,
      showBell,
    ],
  );

  return (
    <InboxContext.Provider value={value}>
      {children}
      {toast ? (
        <Toast
          message={toast}
          type="info"
          duration={5000}
          onClose={() => setToast(null)}
        />
      ) : null}
      <NotificationsDrawer />
      <NotificationPreferencesSheet
        open={preferencesOpen}
        linkId={linkId}
        onClose={() => setPreferencesOpen(false)}
        onRequestSmsConsent={() => void askSmsConsent()}
      />
      <SmsConsentController linkId={linkId} onDone={reopenPreferences} />
    </InboxContext.Provider>
  );
}
